import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { FolderOpen } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';

interface CostCenter {
    id: string;
    name: string;
    address?: string;
    keywords?: string;
    company_id?: string;
    is_default: boolean;
    is_active: boolean;
    has_gdrive_credentials?: boolean;
}

interface Company {
    id: string;
    name: string;
    tax_id?: string;
    is_active: boolean;
}

interface CashRegisterDevice {
    id: string;
    cost_center_id: string;
    ap_number: string;
    name?: string;
    is_active: boolean;
    last_file_date?: string | null;
    last_processed_file_number?: number | null;
}

export default function CostCentersPage() {
    const navigate = useNavigate();
    const params = useParams({ strict: false });
    const routerState = useRouterState();
    const { t } = useI18n();
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [companies, setCompanies] = useState<Company[]>([]);

    // Load cost centers with Google Drive credentials status
    const [devicesByCenter, setDevicesByCenter] = useState<Record<string, CashRegisterDevice[]>>({});


    const loadCostCenters = async () => {
        try {
            setLoading(true);
            const [costCentersResponse, gdriveCredentialsResponse, devicesResponse, companiesResponse] = await Promise.all([
                apiFetch('/api/cost-centers/'),
                apiFetch('/api/gdrive-invoices/credentials/'),
                apiFetch('/api/nav-cash-register/devices/'),
                apiFetch('/api/companies/?include_inactive=false')
            ]);

            if (costCentersResponse.ok) {
                const costCentersData = await costCentersResponse.json();

                // Get Google Drive credentials data if available
                let gdriveCredentialsData = [];
                if (gdriveCredentialsResponse.ok) {
                    gdriveCredentialsData = await gdriveCredentialsResponse.json();
                }

                // Merge credentials status into cost centers
                const costCentersWithCredentials = costCentersData.map((costCenter: CostCenter) => ({
                    ...costCenter,
                    has_gdrive_credentials: gdriveCredentialsData.some((cred: any) =>
                        cred.cost_center_id === costCenter.id && cred.is_active
                    )
                }));

                setCostCenters(costCentersWithCredentials);

                // Load companies
                if (companiesResponse.ok) {
                    const companiesData = await companiesResponse.json();
                    setCompanies(companiesData || []);
                }

                // Process cash register devices by cost center
                let devicesData: CashRegisterDevice[] = [];
                if (devicesResponse.ok) {
                    devicesData = await devicesResponse.json();
                }
                const byCenter: Record<string, CashRegisterDevice[]> = {};
                for (const d of devicesData) {
                    if (!byCenter[d.cost_center_id]) byCenter[d.cost_center_id] = [];
                    byCenter[d.cost_center_id].push(d);
                }
                setDevicesByCenter(byCenter);
            } else {
                console.error('Failed to load cost centers');
            }
        } catch (error) {
            console.error('Error loading cost centers:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCostCenters();
    }, []);

    // Handle form submission


    // Reset form


    // Start creating new cost center
    const startCreate = () => {
        navigate({ to: '/cost-centers/new' as any});
    };

    // Start editing cost center
    const startEdit = (costCenter: CostCenter) => {
        navigate({ to: `/cost-centers/${costCenter.id}`});
    };





    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{t('costCenters.title', 'finance')}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {t('costCenters.subtitle', 'finance')}
                        </p>
                    </div>
                    <Button onClick={startCreate}>
                        {t('actions.addNew', 'common')} {t('costCenters.singular', 'finance')}
                    </Button>
                </div>

                {/* Cost Centers Table */}
                <div>

                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="flex items-center justify-center space-x-2">
                                <Skeleton className="h-4 w-4 rounded-full" />
                                <Skeleton className="h-4 w-[100px]" />
                            </div>
                            <p className="mt-2 text-muted-foreground">Loading cost centers...</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('costCenters.name', 'finance')}</TableHead>
                                    <TableHead>{t('costCenters.address', 'finance')}</TableHead>
                                    <TableHead>Company</TableHead>
                                    <TableHead>{t('costCenters.keywords', 'finance')}</TableHead>
                                    <TableHead>Default</TableHead>
                                    <TableHead>{t('costCenters.isActive', 'finance')}</TableHead>
                                    <TableHead>{t('costCenters.cashRegisters', 'finance')}</TableHead>
                                    <TableHead>{t('gdriveFiles.short', 'finance')}</TableHead>

                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {costCenters.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                                            {t('costCenters.noCostCenters', 'finance')}
                                            <Button
                                                variant="outline"
                                                onClick={startCreate}
                                                className="ml-2"
                                            >
                                                {t('costCenters.createFirst', 'finance')}
                                            </Button>

                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    costCenters.map((costCenter) => {
                                        const company = companies.find(c => c.id === costCenter.company_id);
                                        return (
                                        <TableRow
                                            key={costCenter.id}
                                            className={`cursor-pointer hover:bg-muted/50 ${!costCenter.is_active ? 'opacity-60' : ''}`}
                                            onClick={() => startEdit(costCenter)}
                                        >
                                            <TableCell>
                                                <div className="text-sm font-medium text-foreground">
                                                    {costCenter.name}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-muted-foreground max-w-xs truncate">
                                                    {costCenter.address || 'N/A'}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-muted-foreground">
                                                    {company ? company.name : 'N/A'}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-muted-foreground max-w-xs truncate">
                                                    {costCenter.keywords || 'N/A'}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {costCenter.is_default ? (
                                                    <Badge variant="outline">Default</Badge>
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">No</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={costCenter.is_active ? 'default' : 'secondary'}>
                                                    {costCenter.is_active ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const devs = devicesByCenter[costCenter.id] || [];
                                                    const total = devs.length;
                                                    const active = devs.filter(d => d.is_active).length;
                                                    const last = devs.reduce<string | null>((m, d) => {
                                                        if (!d.last_file_date) return m;
                                                        return !m || new Date(d.last_file_date) > new Date(m) ? d.last_file_date : m;
                                                    }, null);
                                                    return (
                                                        <div className="flex items-center space-x-2">
                                                            <Badge variant={total > 0 ? 'default' : 'outline'}>
                                                                {total} device{total !== 1 ? 's' : ''}
                                                            </Badge>
                                                            <span className="text-xs text-muted-foreground">
                                                                {active}/{total} active{last ? ` \u00b7 Last: ${new Date(last).toLocaleString()}` : ''}
                                                            </span>

                                                        </div>
                                                    );
                                                })()}
                                            </TableCell>

                                            <TableCell>
                                                <div className="flex items-center space-x-2">
                                                    <FolderOpen className={`h-4 w-4 ${costCenter.has_gdrive_credentials ? 'text-green-600' : 'text-gray-400'}`} />
                                                    <Badge variant={costCenter.has_gdrive_credentials ? 'default' : 'outline'}>
                                                        {costCenter.has_gdrive_credentials ? 'Configured' : 'Not Set'}
                                                    </Badge>

                                                </div>
                                            </TableCell>

                                        </TableRow>
                                    );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    )}
                </div>


            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/cost-centers/')({
  component: CostCentersPage,
})
